import avoyaLeadServiceFactory from "@/factories/lead-service/avoya-lead-service-factory";
import newsletterServiceFactory from "@/factories/lead-service/newsletter-service-factory";
import getStatesByLocale from "@/config/states-config";
import { Locale } from "@/i18n/routing";
import { ExperianAddress } from "@/types/experian";
import { BrochureSignupResponse } from "@/types/leads/avoya-request";
import {RABFormData} from "@/types/leads/lead-forms";
import {useState} from "react";
import useGaSessionInfo from "@/hooks/ga-session/use-ga-session";
import { useUserGroups } from "@/hooks/login/useUserGroups";

interface UseRABSubmitProps {
  locale: string;
  sourceUrl: string;
  isLandingPage: boolean;
  marketId: string;
  currencyId: string;
}

const useRABSubmit = ({
  locale,
  sourceUrl,
  isLandingPage,
  marketId,
  currencyId
}: UseRABSubmitProps) => {
  const [serverError, setServerError] = useState<boolean>(false);
  const { isAgent } = useUserGroups();



  const gaData = useGaSessionInfo();

  const onSubmit = async (data: RABFormData) => {
    let experianAddress: ExperianAddress | undefined;

    try {
      if (data.experianAddress?.id) {
        const experianApi = `/api/experian/format?id=${data.experianAddress.id}&locale=${locale}`;
        const experianResponse = await fetch(experianApi, {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        });
        experianAddress = await experianResponse.json();
      }

      const avoyaApi = "/api/avoya-service";
      const newsletterApi = "/api/brochure-service";
      const finalPayload = {
        ...data,
        ...experianAddress,
        state: getStatesByLocale(locale as Locale)
            .find((s) => s.code === experianAddress?.state)?.name
          ?? data.state
      };

      const newsletterServiceRequest = newsletterServiceFactory(finalPayload, marketId, currencyId);
      const newsletterResponse = await fetch(newsletterApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newsletterServiceRequest)
      });
      
      if (isAgent) {
        if (!newsletterResponse.ok) {
          setServerError(true);
        }
        return;
      }

      const brochureData: BrochureSignupResponse = await newsletterResponse.json();
      const avoyaServiceRequest = avoyaLeadServiceFactory(
        finalPayload,
        sourceUrl,
        "RAB",
        isLandingPage,
        locale,
        brochureData,
        gaData
      );

      const [avoyaResponse] = await Promise.all([
        fetch(avoyaApi, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(avoyaServiceRequest)
        }),
        fetch("/api/lead-service?datasource=legacy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(avoyaServiceRequest)
        })
      ]);

      if (!avoyaResponse.ok || !newsletterResponse.ok || !brochureData.isSuccess) {
        setServerError(true);
      }
    } catch (e) {
      setServerError(true);
      console.error("Error posting data:", e);
    }
  };

  return { onSubmit, serverError };
};

export default useRABSubmit;